/**
 * T476 / T477 — OCR confidence badge.
 *
 * Renders a compact pill that surfaces the OCR pipeline's per-attachment
 * or per-field confidence. States (FR-013a / FR-015):
 *   - läuft        → spinner + "läuft"
 *   - validated    → emerald pill, no warning (skipped by default render)
 *   - review       → amber pill "Konfidenz X %"
 *   - low (<60%)   → red pill "Konfidenz X % — manuell prüfen" (FR-015)
 *   - failed       → red pill "OCR fehlgeschlagen — manuell erfassen"
 *   - inactive     → grey pill "OCR nicht aktiv"
 */
import { type ReactNode } from 'react';

export type OcrBadgeKind =
  | 'running'
  | 'validated'
  | 'review'
  | 'low'
  | 'failed'
  | 'inactive';

interface Props {
  kind: OcrBadgeKind;
  confidence?: number; // 0..1 (Tesseract's 0..100 scaled down)
  label?: string;
  testid?: string;
  children?: ReactNode;
}

const PALETTE: Record<
  OcrBadgeKind,
  { bg: string; border: string; text: string; defaultLabel: string }
> = {
  running: {
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    text: 'text-blue-200',
    defaultLabel: 'OCR läuft',
  },
  validated: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    text: 'text-emerald-200',
    defaultLabel: 'OCR validiert',
  },
  review: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    text: 'text-amber-200',
    defaultLabel: 'OCR Review',
  },
  low: {
    bg: 'bg-red-500/10',
    border: 'border-red-500/40',
    text: 'text-red-200',
    defaultLabel: 'Niedrige Konfidenz',
  },
  failed: {
    bg: 'bg-red-500/10',
    border: 'border-red-500/40',
    text: 'text-red-200',
    defaultLabel: 'OCR fehlgeschlagen',
  },
  inactive: {
    bg: 'bg-white/5',
    border: 'border-white/10',
    text: 'text-white/50',
    defaultLabel: 'OCR nicht aktiv',
  },
};

export function OcrConfidenceBadge({ kind, confidence, label, testid, children }: Props) {
  const palette = PALETTE[kind];
  const pct =
    confidence != null ? Math.round((confidence > 1 ? confidence : confidence * 100)) : null;
  const text =
    label ??
    (pct != null && (kind === 'review' || kind === 'low' || kind === 'validated')
      ? `Konfidenz ${pct} %`
      : palette.defaultLabel);
  return (
    <span
      data-testid={testid ?? `ocr-badge-${kind}`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded border ${palette.bg} ${palette.border} ${palette.text} tabular-nums`}
    >
      {children}
      {text}
      {kind === 'low' && pct != null && <span title="manuelle Prüfung empfohlen">⚠</span>}
      {kind === 'failed' && <span aria-hidden>⚠</span>}
    </span>
  );
}

/**
 * Pure helper: classify an attachment's confidence into a badge kind.
 * Returns null when the row has no OCR signal (eRechnung-validated, no
 * pipeline run yet, etc.) so the caller can skip rendering entirely.
 */
export function classifyAttachmentConfidence(input: {
  isErechnungXml?: boolean;
  ocrFieldConfidences?: Record<string, number> | null;
}): { kind: OcrBadgeKind; confidence: number } | null {
  if (input.isErechnungXml) return null;
  const fc = input.ocrFieldConfidences;
  if (!fc || Object.keys(fc).length === 0) return null;
  const values = Object.values(fc).filter((v) => typeof v === 'number');
  if (values.length === 0) return null;
  const avg = values.reduce((s, v) => s + v, 0) / values.length;
  const norm = avg > 1 ? avg / 100 : avg;
  if (norm < 0.6) return { kind: 'low', confidence: norm };
  if (norm < 0.85) return { kind: 'review', confidence: norm };
  return { kind: 'validated', confidence: norm };
}
